/** Background colour thresholds for the score chip.
 *  Green ≥ 8.5 · Amber ≥ 7.0 · Red < 7.0. */
const BG_COLOR = {
  green: '#E8F5E9',
  amber: '#FFF4E5',
  red: '#FDECEA',
} as const;

const TEXT_COLOR = '#5B5B7A';

interface Props {
  /** Numeric score or a string that will be parsed to a number for threshold logic. */
  score: number | string;
  /** Automation hook: chip id; the numeric text gets `${testId}-value`. */
  testId?: string;
}

/** Compact chip that colour-codes a numeric score.
 *  Green (≥ 8.5), amber (≥ 7.0), or red (< 7.0). Accepts string scores and
 *  parses them internally so callers don't need to coerce. */
export function ScoreChip({ score, testId = 'score-chip' }: Props) {
  const numeric = typeof score === 'string' ? parseFloat(score) : score;

  const bgColor = isNaN(numeric)
    ? BG_COLOR.red
    : numeric >= 8.5
      ? BG_COLOR.green
      : numeric >= 7.0
        ? BG_COLOR.amber
        : BG_COLOR.red;

  return (
    <div
      data-testid={testId}
      className="inline-flex items-center justify-center gap-2 rounded-lg"
      style={{
        minWidth: 37,
        height: 32,
        paddingTop: 8,
        paddingRight: 12,
        paddingBottom: 8,
        paddingLeft: 12,
        backgroundColor: bgColor,
        color: TEXT_COLOR,
      }}
    >
      <span className="text-sm leading-none font-medium" data-testid={`${testId}-value`}>
        {isNaN(numeric) ? '—' : String(numeric)}
      </span>
    </div>
  );
}
